import React, { useContext, useState, useEffect } from 'react';
import moment from 'moment';
import { InputContainer } from './InputContainer.js';
import FormField from './FormField.js';
import { constants, formModes, formFieldTypes, transportationMethods, alfrescoTypes, UserContext, customEventTypes, settingsValueFieldTypes, tripDestinations } from '../../constants.js';
import { Button, OverlayTrigger, Tooltip, Accordion } from 'react-bootstrap';
import { yesNoDialog } from '../ModalDialog.js';
import i18next from "../../i18n.js";
import { getWordWithCorrectEnding, getSubformHeaderPreview, getSubformHeaderInfo } from '../helpers/HelperFunctions.js';
import { findField, isFieldVisibleOnStep, getFieldValue, setFieldValue, toDateLimit, isDateOutOfRange, getRouteDepartureDate, getResidenceDateRange } from '../documents/DocumentHelpers.js';
import { eventDispatcher } from '../../eventDispatcher.js';
import { checkRouteForm, checkReportForms, checkOperationForms, checkResultForms } from '../helpers/CheckFunctions.js';

const SubForm = (props) => {
    const userContext = useContext(UserContext);
    const [expanded, setExpanded] = useState(false);
    const [expandedChildren, setExpandedChildren] = useState([]);

    useEffect(() => {
        const onSubformErrorId = eventDispatcher.subscrubeToEvent(customEventTypes.onSubformError, (params) => {
            const name = props.name?.split('---').pop();
            if (params.subformName === name) {
                switch (name) {
                    case 'carInfoForm':
                    case 'travelLetterCarInfo':
                        setExpanded(true);
                        break;
                    case 'operationForm':
                    case 'reportForm':
                    case 'resultForm':
                        if (!expandedChildren.includes(params.index)) {
                            setExpandedChildren(prevValue => [...prevValue, params.index]);
                        }
                        break;
                }
            }
            if (params.subform) {
                eventDispatcher.invokeEvent(customEventTypes.onSubformError, {
                    subformName: params.subform.subformName,
                    index: params.subform.formIndex,
                    subform: params.subform.subform
                });
            }
        });

        return () => {
            eventDispatcher.unsubscrubeFromEvent(onSubformErrorId);
        }
    }, []);

    useEffect(() => {
        switch (props.name) {
            case 'travelLetterCarInfo':
                if (props.formMode === formModes.read) {
                    setExpanded(false);
                }
                break;
            case 'operationForm':
                if (props.formMode === formModes.edit && props.value?.length > 0) {
                    if (!checkOperationForms({
                        properties: {
                            operationForm: props.value
                        }
                    }).result) {
                        setExpandedChildren([props.value.length - 1]);
                    }
                }
                break;
            case 'reportForm':
                if (props.formMode === formModes.edit && props.value?.length > 0) {
                    if (!checkReportForms({
                        properties: {
                            reportForm: props.value
                        }
                    }).result) {
                        setExpandedChildren([props.value.length - 1]);
                    }
                }
                break;
            case 'resultForm':
                if (props.formMode === formModes.edit && props.value?.length > 0) {
                    if (!checkResultForms({
                        properties: {
                            resultForm: props.value
                        }
                    }).result) {
                        setExpandedChildren([props.value.length - 1]);
                    }
                }
                break;
            case 'routeForm':
                if (props.formMode === formModes.edit && props.value?.length > 0) {
                    const expanded = [];
                    props.value.forEach((value, index) => {
                        if (!checkRouteForm({
                            properties: {
                                routeForm: props.value
                            }
                        }, index, 'routeForm').result) {
                            expanded.push(index);
                        }
                    })

                    if (expanded.length > 0) {
                        setExpandedChildren(expanded);
                    }
                }
                break;
        }
    }, [props.formMode]);

    // Межі дат маршруту та вікно його проживання. Ті самі межі обмежують поля проживання
    // і показуються в заголовку елемента, тому рахуються в одному місці
    const getRouteDateLimits = (index, formName) => {
        // Дати маршруту вибираються в межах дат відрядження (minDate/maxDate приходять
        // з документа), а кожен наступний маршрут не може виїжджати раніше попереднього
        let routeMinDate = toDateLimit(props.settings.minDate);
        for (let i = 0; i < index; i++) {
            const previousDepartureDate = getRouteDepartureDate(props.value[i]);
            if (previousDepartureDate) routeMinDate = toDateLimit(previousDepartureDate);
        }
        const routeMaxDate = toDateLimit(props.settings.maxDate);

        // проживання обмежене не всім відрядженням, а відрізком саме цього маршруту;
        // у формі повернення власного відрізка немає, тому лишаються межі відрядження
        const residenceRange = formName === 'goingBackForm'
            ? {}
            : getResidenceDateRange(props.value, index, props.settings.goingBackForm, props.settings.multiple);
        return {
            routeMinDate,
            routeMaxDate,
            residenceRange,
            residenceMinDate: toDateLimit(residenceRange.from) ?? routeMinDate,
            residenceMaxDate: toDateLimit(residenceRange.to) ?? routeMaxDate
        };
    }

    // Межі дат транзитної зупинки та вікно її проживання: зупинка не може бути раніше за
    // попередню, а вся послідовність лежить у межах маршруту. Проживання — від дати своєї
    // зупинки до дати наступної, а для останньої зупинки — до виїзду назад
    const getTransitDateLimits = (index) => {
        let transitMinDate = toDateLimit(props.settings.minDate);
        for (let i = 0; i < index; i++) {
            const previousTransitDate = props.value[i].properties['transitDate'];
            if (previousTransitDate) transitMinDate = toDateLimit(previousTransitDate);
        }
        const transitMaxDate = toDateLimit(props.settings.maxDate);
        return {
            transitMinDate,
            transitMaxDate,
            residenceMinDate: toDateLimit(props.value[index].properties['transitDate']) ?? transitMinDate,
            residenceMaxDate: toDateLimit(props.value[index + 1]?.properties['transitDate']) ?? transitMaxDate
        };
    }

    // Прибирає з пункту маршруту «Місто, Область» назву області. Відсікається лише суфікс,
    // що збігається з назвою області зі словника, щоб не зачепити кому в самій назві міста
    const stripRegion = (point) => {
        if (!point) return point;
        const placeInfo = props.settings.fields.find(f => f.name === 'placeInfo');
        const regions = placeInfo?.fields.find(f => f.name === 'sx:pbtRegion')?.values?.data ?? [];
        const region = regions.find(item => item.label && point.endsWith(`, ${item.label}`));
        return region ? point.slice(0, point.length - region.label.length - 2) : point;
    }

    // Підпис відрізка в заголовку елемента проживання: «Звідки → Куди (початок – кінець)»,
    // де дати — межі полів заселення й виселення
    const getResidenceHeaderText = (fromCity, toCity, minDate, maxDate) => {
        const cities = [fromCity, toCity].filter(Boolean).join(' → ');
        const dates = [minDate, maxDate].filter(Boolean).map(date => moment(date).format('DD.MM.YYYY')).join(' – ');
        return [cities, dates && (cities ? `(${dates})` : dates)].filter(Boolean).join(' ');
    }

    const getFields = (index) => {
        let fields = structuredClone(props.settings.fields.filter(f => f.visible));
        const formName = props.name.split('---').pop().split('_final')[0];
        switch (formName) {
            case 'routeFormMain':
            case 'routeForm':
            case 'goingBackForm': {
                const id = props.value[index].properties['sys:node-uuid'];

                const residenceStatusFormField = fields.find(f => f.name === 'residenceStatusForm');
                residenceStatusFormField.visible = props.value[index].properties['sx:btAccommodationRequired'];

                const { routeMinDate, routeMaxDate, residenceRange, residenceMinDate, residenceMaxDate } = getRouteDateLimits(index, formName);
                residenceStatusFormField.minDate = residenceMinDate;
                residenceStatusFormField.maxDate = residenceMaxDate;

                const autoOwnerSection = fields.find(f => f.name === 'autoOwnerSection');
                if (autoOwnerSection) {
                    const colleagueField = autoOwnerSection.fields.find(f => f.name === 'assoc:sx:pbtColleague_added');
                    autoOwnerSection.visible = props.value[index].properties['sx:pbtMethodTransportation'] === transportationMethods.autoColleagues;
                    colleagueField.visible = props.value[index].properties['sx:pbtMethodTransportation'] === transportationMethods.autoColleagues;
                }

                const corporateCarInfo = fields.find(f => f.name === 'corporateCarInfo');
                const carInfoFormField = fields.find(f => f.name === 'carInfoForm');

                if (corporateCarInfo) {
                    corporateCarInfo.visible = props.value[index].properties['sx:pbtMethodTransportation'] === transportationMethods.corporateCar;
                }
                if (carInfoFormField) {
                    carInfoFormField.visible = props.value[index].properties['sx:pbtMethodTransportation'] === transportationMethods.ownCar || props.value[index].properties['sx:pbtMethodTransportation'] === transportationMethods.carRental;
                    if (props.value[index].properties['sx:pbtMethodTransportation'] === transportationMethods.ownCar) {
                        carInfoFormField.userNodeRef = props.settings.userNodeRef;
                        carInfoFormField.userName = props.settings.userName;
                    }
                    const ownCarAttachmentsField = carInfoFormField.fields.find(f => f.name === 'ownCarAttachments');
                    ownCarAttachmentsField.visible = props.value[index].properties['sx:pbtMethodTransportation'] === transportationMethods.ownCar;

                    const rentalCarBillAttachmentsField = carInfoFormField.fields.find(f => f.name === 'rentalCarBillAttachments');
                    rentalCarBillAttachmentsField.visible = props.value[index].properties['sx:pbtMethodTransportation'] === transportationMethods.carRental;

                    const rentalCarActAttachmentsField = carInfoFormField.fields.find(f => f.name === 'rentalCarActAttachments');
                    rentalCarActAttachmentsField.visible = props.value[index].properties['sx:pbtMethodTransportation'] === transportationMethods.carRental;
                }

                // транзитні зупинки й транзитне проживання живуть у тому самому проміжку,
                // що й маршрут: між виїздом туди і виїздом назад
                fields.filter(f => f.name === 'transitForm').forEach(field => {
                    field.userNodeRef = props.settings.userNodeRef;
                    field.userName = props.settings.userName;
                    // звідки їдемо до першої зупинки — для підпису в заголовку проживання
                    field.departurePoint = props.value[index].properties['travelForm']?.[0]?.properties['sx:tfPoint'];
                    field.minDate = toDateLimit(residenceRange.from) ?? routeMinDate;
                    field.maxDate = toDateLimit(residenceRange.to) ?? routeMaxDate;
                });


                const routeBackInfoSection = fields.find(f => f.name === 'routeBackInfoSection');

                if (routeBackInfoSection) {
                    routeBackInfoSection.visible = !props.value[index].properties['sx:pbtSameTransportBack'] && props.value[index].properties['sx:pbtBackMethodTransportation'] && props.value[index].properties['sx:pbtMethodTransportation'];
                    const backTravelPreferencesField = routeBackInfoSection.fields.find(f => f.name === 'backTravelPreferences');
                    backTravelPreferencesField.visible = props.value[index].properties['sx:pbtBackMethodTransportation'] !== transportationMethods.ownCar;

                    const placeBackStartEndDateField = routeBackInfoSection.fields.find(f => f.name === 'placeBackStartEndDate');
                    placeBackStartEndDateField.settings.hideEndDate = true;
                    // назад не можна виїхати раніше, ніж туди, і пізніше кінця відрядження
                    placeBackStartEndDateField.minValue = toDateLimit(getRouteDepartureDate(props.value[index])) ?? routeMinDate;
                    placeBackStartEndDateField.maxValue = routeMaxDate;

                    const carInfoBackForm = routeBackInfoSection.fields.find(f => f.name === 'carInfoBackForm');
                    carInfoBackForm.visible = props.value[index].properties['sx:pbtBackMethodTransportation'] === transportationMethods.ownCar || props.value[index].properties['sx:pbtBackMethodTransportation'] === transportationMethods.carRental;

                    if (props.value[index].properties['sx:pbtBackMethodTransportation'] === transportationMethods.ownCar) {
                        carInfoBackForm.userNodeRef = props.settings.userNodeRef;
                        carInfoBackForm.userName = props.settings.userName;
                    }

                    const ownCarAttachmentsBackField = carInfoBackForm.fields.find(f => f.name === 'ownCarAttachments');
                    ownCarAttachmentsBackField.visible = props.value[index].properties['sx:pbtBackMethodTransportation'] === transportationMethods.ownCar;

                    const rentalCarBillAttachmentsBackField = carInfoBackForm.fields.find(f => f.name === 'rentalCarBillAttachments');
                    rentalCarBillAttachmentsBackField.visible = props.value[index].properties['sx:pbtBackMethodTransportation'] === transportationMethods.carRental;

                    const rentalCarActAttachmentsBackField = carInfoBackForm.fields.find(f => f.name === 'rentalCarActAttachments');
                    rentalCarActAttachmentsBackField.visible = props.value[index].properties['sx:pbtBackMethodTransportation'] === transportationMethods.carRental;

                    const autoOwnerBackSection = routeBackInfoSection.fields.find(f => f.name === 'autoOwnerBackSection');
                    autoOwnerBackSection.visible = props.value[index].properties['sx:pbtBackMethodTransportation'] === transportationMethods.autoColleagues;

                    const corporateCarInfoBack = routeBackInfoSection.fields.find(f => f.name === 'corporateCarInfoBack');
                    corporateCarInfoBack.visible = props.value[index].properties['sx:pbtBackMethodTransportation'] === transportationMethods.corporateCar;
                }

                const methodTransportation = props.value[index].properties['sx:pbtMethodTransportation'];

                const sameTransportBackField = fields.find(f => f.name === 'sx:pbtSameTransportBack');
                const transportCommentField = fields.find(f => f.name === 'sx:pbtComment');
                const travelFormField = fields.find(f => f.name === 'travelForm');
                transportCommentField.visible = travelFormField.visible = !!methodTransportation;

                if (sameTransportBackField) {
                    sameTransportBackField.visible = !!methodTransportation;
                }

                const backTransportSection = fields.find(f => f.name === 'backTransportSection');
                backTransportSection.visible = sameTransportBackField?.visible && !props.value[index].properties['sx:pbtSameTransportBack'] && props.value[index].properties['sx:pbtMethodTransportation'];

                const placeStartEndDateField = travelFormField.fields.find(f => f.name === 'placeStartEndDate');
                if (placeStartEndDateField) {
                    placeStartEndDateField.settings.hideEndDate = !props.value[index].properties['sx:pbtSameTransportBack'] || formName === 'goingBackForm';
                    placeStartEndDateField.minValue = routeMinDate;
                    placeStartEndDateField.maxValue = routeMaxDate;
                }
                const placeStartEndDateMultipleField = travelFormField.fields.find(f => f.name === 'placeStartEndDateMultiple');
                if (placeStartEndDateMultipleField) {
                    placeStartEndDateMultipleField.minValue = routeMinDate;
                    placeStartEndDateMultipleField.maxValue = routeMaxDate;
                }
                const preferredDepartureTimeField = travelFormField.fields.find(f => f.name === 'sx:tfPreferredDepartureTime');
                if (preferredDepartureTimeField) {
                    preferredDepartureTimeField.visible = props.value[index].properties['sx:pbtMethodTransportation'] !== transportationMethods.ownCar;
                }

                const methodTransportationField = fields.find(f => f.name === 'sx:pbtMethodTransportation');
                if (props.settings.hideTransportLabel) {
                    methodTransportationField.label = '';
                }
                if (props.settings.hideTransportSelect) {
                    methodTransportationField.visible = false;
                }

                const placeInfo = fields.find(f => f.name === 'placeInfo');
                const tripGoalField = placeInfo.fields.find(f => f.name === 'sx:pbtTripGoal');
                const tripGoalCommentField = placeInfo.fields.find(f => f.name === 'sx:pbtTripGoalComment');
                tripGoalCommentField.visible = tripGoalField.visible ? (props.value[index].properties['sx:pbtTripGoal'] === constants.otherTripGoalValue) : (props.value[index].properties['sx:pbtTripGoalForeign'] === constants.otherTripGoalValue);

                break;
            }
            case 'transitForm': {
                const autoOwnerTransitSection = fields.find(f => f.name === 'autoOwnerTransitSection');
                if (autoOwnerTransitSection) {
                    autoOwnerTransitSection.visible = props.value[index].properties['sx:pbtMethodTransportation'] === transportationMethods.autoColleagues;

                    const id = props.value[index].properties['sys:node-uuid'];

                    const colleagueField = autoOwnerTransitSection.fields.find(f => f.name === 'assoc:sx:pbtColleague_added');
                    const colleagueAddedField = autoOwnerTransitSection.fields.find(f => f.name === 'sx:pbtColleague');
                    colleagueField.visible = !!id;
                    colleagueAddedField.visible = !id;

                }

                const corporateCarTransitInfo = fields.find(f => f.name === 'corporateCarTransitInfo');
                corporateCarTransitInfo.visible = props.value[index].properties['sx:pbtMethodTransportation'] === transportationMethods.corporateCar;

                const carInfoFormField = fields.find(f => f.name === 'carInfoForm');
                carInfoFormField.visible = props.value[index].properties['sx:pbtMethodTransportation'] === transportationMethods.ownCar || props.value[index].properties['sx:pbtMethodTransportation'] === transportationMethods.carRental;

                if (props.value[index].properties['sx:pbtMethodTransportation'] === transportationMethods.ownCar) {
                    carInfoFormField.userNodeRef = props.settings.userNodeRef;
                    carInfoFormField.userName = props.settings.userName;
                }

                const ownCarAttachmentsField = carInfoFormField.fields.find(f => f.name === 'ownCarAttachments');
                ownCarAttachmentsField.visible = props.value[index].properties['sx:pbtMethodTransportation'] === transportationMethods.ownCar;

                const rentalCarBillAttachmentsField = carInfoFormField.fields.find(f => f.name === 'rentalCarBillAttachments');
                rentalCarBillAttachmentsField.visible = props.value[index].properties['sx:pbtMethodTransportation'] === transportationMethods.carRental;

                const rentalCarActAttachmentsField = carInfoFormField.fields.find(f => f.name === 'rentalCarActAttachments');
                rentalCarActAttachmentsField.visible = props.value[index].properties['sx:pbtMethodTransportation'] === transportationMethods.carRental;

                const residenceStatusFormField = fields.find(f => f.name === 'residenceStatusForm');
                residenceStatusFormField.visible = props.value[index].properties['sx:btAccommodationRequired'];

                const { transitMinDate, transitMaxDate, residenceMinDate, residenceMaxDate } = getTransitDateLimits(index);

                // дата зупинки лежить у групі полів, тому шукаємо її саме там
                const transitTripPlaceInfo = fields.find(f => f.name === 'transitTripPlaceInfo');
                const transitDateField = transitTripPlaceInfo?.fields.find(f => f.name === 'transitDate');
                if (transitDateField) {
                    transitDateField.minValue = transitMinDate;
                    transitDateField.maxValue = transitMaxDate;
                }

                residenceStatusFormField.minDate = residenceMinDate;
                residenceStatusFormField.maxDate = residenceMaxDate;

                break;
            }
            case 'residenceStatusForm': {
                // проживання можливе лише в межах дат маршруту, які приходять з документа
                const residenceStartEndDateField = fields.find(f => f.name === 'residenceStartEndDate');
                if (residenceStartEndDateField) {
                    residenceStartEndDateField.minValue = toDateLimit(props.settings.minDate);
                    residenceStartEndDateField.maxValue = toDateLimit(props.settings.maxDate);
                }
                break;
            }
            case 'carInfoBackForm':
            case 'carInfoForm':
            case 'carInfoForm_forAll': {
                const carInfoUpdateField = fields.find(f => f.name === 'carInfoUpdate');
                carInfoUpdateField.visible = isNeedUpdateCarInfo(props.value[index].properties);

                const ownCarInfoField = fields.find(f => f.name === 'ownCarInfo');
                const ownCarInfoUpdateField = ownCarInfoField.fields.find(f => f.name === 'carInfoUpdate');
                ownCarInfoUpdateField.visible = isNeedUpdateCarInfo(props.value[index].properties);

                let fluentTypeFields = fields.filter(f => f.name.startsWith('sx:ciFuelType_'));
                fluentTypeFields.forEach(field => {
                    field.visible = props.value[index].properties['sx:ciEngineType'] && field.name.endsWith(props.value[index].properties['sx:ciEngineType']);
                });
                const ownCarInfo = fields.find(f => f.name === 'ownCarInfo');
                if (ownCarInfo) {
                    fluentTypeFields = ownCarInfo.fields.filter(f => f.name.startsWith('sx:ciFuelType_'));
                    fluentTypeFields.forEach(field => {
                        field.visible = props.value[index].properties['sx:ciEngineType'] && field.name.endsWith(props.value[index].properties['sx:ciEngineType']);
                    });
                }

                break;
            }
            case 'operationForm': {
                const odometrEndField = fields.find(f => f.name === 'sx:opOdometerEnd');

                const odometrStart = props.value[index].properties['sx:opOdometerStart'];
                const odometrEnd = props.value[index].properties['sx:opOdometerEnd'];
                if (odometrStart && odometrEnd) {
                    const odometrStartValue = parseFloat(odometrStart.replaceAll(' ', '').replace(',', '.'));
                    const odometrEndValue = parseFloat(odometrEnd.replaceAll(' ', '').replace(',', '.'));
                    if (odometrEndValue - odometrStartValue < 0.000009) {
                        odometrEndField.errorText = i18next.t('document.operationFormOdometrError');
                    }
                    else {
                        odometrEndField.errorText = '';
                    }
                }
                else {
                    odometrEndField.errorText = '';
                }
                if (odometrStart) {
                    const odometrStartField = fields.find(f => f.name === 'sx:opOdometerStart');
                    const odometrStartValue = parseFloat(odometrStart.replaceAll(' ', '').replace(',', '.'));
                    for (let i = 0; i < index; i++) {
                        const odometrEndPeriod = props.value[i].properties['sx:opOdometerEnd'];
                        if (odometrEndPeriod) {
                            const odometrEndPeriodValue = parseFloat(odometrEndPeriod.replaceAll(' ', '').replace(',', '.'));
                            if (odometrStartValue - odometrEndPeriodValue < -0.000009) {
                                odometrStartField.errorText = i18next.t('document.optionFormOdometrItemError');
                                break;
                            }
                        }
                    }
                }

                const attachmentsFormField = fields.find(f => f.name === 'sx:attachments');
                attachmentsFormField.docId = props.value[index].properties['sys:node-uuid'];

                break;
            }
            case 'carInformationForm': {
                const fluentTypeFields = fields.filter(f => f.name.startsWith('sxu:fuelType_'));
                fluentTypeFields.forEach(field => {
                    field.visible = props.value[index].properties['sxu:engineType'] && field.name.endsWith(props.value[index].properties['sxu:engineType']);
                });
            }
            case 'travelLetterCarInfo': {
                const fluentTypeFields = fields.filter(f => f.name.startsWith('sx:ciFuelType_'));
                fluentTypeFields.forEach(field => {
                    field.visible = props.value[index].properties['sx:ciEngineType'] && field.name.endsWith(props.value[index].properties['sx:ciEngineType']);
                });
            }
            default:
                break;
        }
        if (props.value[index].properties['sx:autofillObject']) {
            fields = fields.filter(f => f.showAutofill);
            fields.forEach(field => {
                field.disabled = true;
            });
        }
        return fields;
    }

    const updateValues = (data) => {
        switch (props.name.split('---').pop()) {
            case 'operationForm': {
                const odometrStart = data.properties['sx:opOdometerStart'];
                const odometrEnd = data.properties['sx:opOdometerEnd'];
                if (odometrStart && odometrEnd) {
                    const odometrStartValue = parseFloat(odometrStart.replaceAll(' ', '').replace(',', '.'));
                    const odometrEndValue = parseFloat(odometrEnd.replaceAll(' ', '').replace(',', '.'));
                    if (odometrEndValue - odometrStartValue > -0.000009) {
                        data.properties['sx:opDistanceTraveled'] = (odometrEndValue - odometrStartValue).toFixed(2).replace('.', ',');
                    }
                    else {
                        data.properties['sx:opDistanceTraveled'] = '0';
                    }
                }
                else {
                    data.properties['sx:opDistanceTraveled'] = '0';
                }
                break;
            }
            default:
                break;
        }
    }

    const onChange = (e, index, updatedFields = []) => {
        if (props.value && props.onChange) {
            const { name, value, checked } = e.target;
            const newValue = e.target.type === 'checkbox' ? checked : value;

            const updatetdValue = structuredClone(props.value);
            const fieldName = name.split(`${props.name}---${index}---`).pop();
            updatedFields.push({
                name: fieldName,
                index: index,
            });

            const oldValue = updatetdValue[index].properties[fieldName];
            // поля, які редагують кілька властивостей одразу, повертають їх значення об'єктом
            if (settingsValueFieldTypes.includes(findField(props.settings.fields, fieldName)?.type)) {
                setFieldValue(updatetdValue[index].properties, newValue);
            }
            else {
                updatetdValue[index].properties[fieldName] = newValue;
            }
            updatetdValue[index].properties['wasUpdated'] = true;
            if (fieldName === 'transitDate') {
                if (updatetdValue[index].properties['travelForm']?.length > 0) {
                    updatetdValue[index].properties['travelForm'][0].properties['sx:tfDepartureDate'] = newValue;
                    updatetdValue[index].properties['travelForm'][0].properties['wasUpdated'] = true;
                }
            }

            const clearCarInfoForm = (form) => {
                if (form?.length > 0) {
                    const carInfoForm = form[0];
                    if (carInfoForm.properties['sx:ciEngineType']) {
                        carInfoForm.properties[`sx:ciFuelType_${carInfoForm.properties['sx:ciEngineType']}`] = '';
                    }
                    carInfoForm.properties['sx:ciBrandCar'] = '';
                    carInfoForm.properties['sx:ciRegNumerInfo'] = '';
                    carInfoForm.properties['sx:ciReleaseYear'] = '';
                    carInfoForm.properties['sx:ciEngineType'] = '';
                    carInfoForm.properties['sx:ciEngineDisplacement'] = '';
                    carInfoForm.properties['sx:ciCarColor'] = '';
                }
                else if (form) {
                    form = [{ type: 'sx:CarInfo', properties: {} }];
                }
            }

            if ((props.name === 'routeForm' || props.name === 'goingBackForm') &&
                fieldName === 'sx:pbtMethodTransportation' &&
                (value === transportationMethods.carRental || !value)
            ) {
                clearCarInfoForm(updatetdValue[index].properties['carInfoForm']);
            }
            if ((props.name === 'routeForm' || props.name === 'goingBackForm') &&
                fieldName === 'sx:pbtBackMethodTransportation' &&
                (value === transportationMethods.carRental || !value)
            ) {
                clearCarInfoForm(updatetdValue[index].properties['carInfoBackForm']);
            }
            if (props.name.endsWith('---transitForm') &&
                fieldName === 'sx:pbtMethodTransportation' &&
                (value !== transportationMethods.ownCar || !value)
            ) {
                clearCarInfoForm(updatetdValue[index].properties['carInfoForm']);
            }

            if ((props.name === 'routeForm' || props.name === 'goingBackForm') &&
                fieldName === 'sx:pbtMethodTransportation' &&
                value === transportationMethods.ownCar &&
                (props.settings.userNodeRef === userContext?.nodeRef || props.settings.userName === userContext?.login) &&
                userContext?.userCarInfo
            ) {
                const carInfoForm = updatetdValue[index].properties['carInfoForm']?.length > 0 ? updatetdValue[index].properties['carInfoForm'][0] : null;
                if (carInfoForm && !['sx:ciBrandCar', 'sx:ciRegNumerInfo', 'sx:ciReleaseYear', 'sx:ciEngineType', 'sx:ciEngineDisplacement', 'sx:ciCarColor'].some(key => carInfoForm.properties[key])) {
                    carInfoForm.properties['sx:ciBrandCar'] = userContext.userCarInfo.brand;
                    carInfoForm.properties['sx:ciRegNumerInfo'] = userContext.userCarInfo.number;
                    carInfoForm.properties['sx:ciReleaseYear'] = userContext.userCarInfo.releaseYear;
                    carInfoForm.properties['sx:ciEngineType'] = userContext.userCarInfo.engineType;
                    carInfoForm.properties['sx:ciEngineDisplacement'] = userContext.userCarInfo.engineVolume;
                    carInfoForm.properties['sx:ciCarColor'] = userContext.userCarInfo.color;
                    if (userContext.userCarInfo.engineType) {
                        carInfoForm.properties[`sx:ciFuelType_${userContext.userCarInfo.engineType}`] = userContext.userCarInfo.fuelType;
                    }
                    carInfoForm.properties['wasUpdated'] = true;
                }
            }
            if ((props.name.endsWith('---transitForm')) &&
                fieldName === 'sx:pbtMethodTransportation' &&
                value === transportationMethods.ownCar &&
                (props.settings.userNodeRef === userContext?.nodeRef || props.settings.userName === userContext?.login) &&
                userContext?.userCarInfo
            ) {
                const carInfoForm = updatetdValue[index].properties['carInfoForm']?.length > 0 ? updatetdValue[index].properties['carInfoForm'][0] : null;
                if (carInfoForm && !['sx:ciBrandCar', 'sx:ciRegNumerInfo', 'sx:ciReleaseYear', 'sx:ciEngineType', 'sx:ciEngineDisplacement', 'sx:ciCarColor'].some(key => carInfoForm.properties[key])) {
                    carInfoForm.properties['sx:ciBrandCar'] = userContext.userCarInfo.brand;
                    carInfoForm.properties['sx:ciRegNumerInfo'] = userContext.userCarInfo.number;
                    carInfoForm.properties['sx:ciReleaseYear'] = userContext.userCarInfo.releaseYear;
                    carInfoForm.properties['sx:ciEngineType'] = userContext.userCarInfo.engineType;
                    carInfoForm.properties['sx:ciEngineDisplacement'] = userContext.userCarInfo.engineVolume;
                    carInfoForm.properties['sx:ciCarColor'] = userContext.userCarInfo.color;
                    if (userContext.userCarInfo.engineType) {
                        carInfoForm.properties[`sx:ciFuelType_${userContext.userCarInfo.engineType}`] = userContext.userCarInfo.fuelType;
                    }
                    carInfoForm.properties['wasUpdated'] = true;
                }
            }
            if ((props.name === 'routeForm' || props.name === 'goingBackForm') &&
                fieldName === 'sx:pbtBackMethodTransportation' &&
                value === transportationMethods.ownCar &&
                (props.settings.userNodeRef === userContext?.nodeRef || props.settings.userName === userContext?.login) &&
                userContext?.userCarInfo
            ) {
                const carInfoBackForm = updatetdValue[index].properties['carInfoBackForm']?.length > 0 ? updatetdValue[index].properties['carInfoBackForm'][0] : null;
                if (carInfoBackForm && !['sx:ciBrandCar', 'sx:ciRegNumerInfo', 'sx:ciReleaseYear', 'sx:ciEngineType', 'sx:ciEngineDisplacement', 'sx:ciCarColor'].some(key => carInfoBackForm.properties[key])) {
                    carInfoBackForm.properties['sx:ciBrandCar'] = userContext.userCarInfo.brand;
                    carInfoBackForm.properties['sx:ciRegNumerInfo'] = userContext.userCarInfo.number;
                    carInfoBackForm.properties['sx:ciReleaseYear'] = userContext.userCarInfo.releaseYear;
                    carInfoBackForm.properties['sx:ciEngineType'] = userContext.userCarInfo.engineType;
                    carInfoBackForm.properties['sx:ciEngineDisplacement'] = userContext.userCarInfo.engineVolume;
                    carInfoBackForm.properties['sx:ciCarColor'] = userContext.userCarInfo.color;
                    if (userContext.userCarInfo.engineType) {
                        carInfoBackForm.properties[`sx:ciFuelType_${userContext.userCarInfo.engineType}`] = userContext.userCarInfo.fuelType;
                    }
                    carInfoBackForm.properties['wasUpdated'] = true;
                }
            }
            if ((props.name === 'operationForm' || props.name === 'reportForm') && fieldName === 'sx:attachments') {
                if (oldValue?.length > 0) {
                    const forRemove = oldValue.filter(item => item.nodeRef && (!value || !value.some(it => it.nodeRef === item.nodeRef)));
                    if (forRemove?.length > 0) {
                        const removedItems = (updatetdValue[index].properties['assoc:sx:additionalFiles_removed'] ?? '').split(',');
                        updatetdValue[index].properties['assoc:sx:additionalFiles_removed'] = removedItems.concat(forRemove.map(it => it.nodeRef)).join(',');
                    }
                }
            }
            if (props.name === 'routeForm' && fieldName === 'transitForm') {
                if (oldValue?.length > 0) {
                    const forRemove = oldValue.filter(item => item.nodeRef && (!value || !value.some(it => it.nodeRef === item.nodeRef)));
                    if (forRemove?.length > 0) {
                        const removedItems = (updatetdValue[index].properties['assoc:sx:bptTransitTripPlaceAssoc_removed'] ?? '').split(',');
                        updatetdValue[index].properties['assoc:sx:bptTransitTripPlaceAssoc_removed'] = removedItems.concat(forRemove.map(it => it.nodeRef)).join(',');
                    }
                }
            }
            if ((props.name === 'routeForm' || props.name === 'goingBackForm') && (fieldName === 'sx:pbtRegion' || fieldName === 'sx:pbtLocality')) {
                if (updatetdValue[index].properties['sx:pbtRegion'] && updatetdValue[index].properties['sx:pbtLocality']) {
                    const travelForm = updatetdValue[index].properties['travelForm'];
                    if (travelForm?.length > 0) {
                        const placeInfo = props.settings.fields.find(f => f.name === 'placeInfo');
                        const selectedItem = placeInfo.fields.find(f => f.name === 'sx:pbtRegion').values.data.find(item => item.value === updatetdValue[index].properties['sx:pbtRegion']);
                        travelForm[0].properties['sx:tfPointArrival'] = `${updatetdValue[index].properties['sx:pbtLocality']}, ${selectedItem?.label}`;
                        travelForm[0].properties['wasUpdated'] = true;

                        if (index < updatetdValue.length - 1) {
                            const nextTravelForm = updatetdValue[index + 1].properties['travelForm'];
                            if (nextTravelForm?.length > 0) {
                                nextTravelForm[0].properties['sx:tfPoint'] = travelForm[0].properties['sx:tfPointArrival'];
                                nextTravelForm[0].properties['wasUpdated'] = true;
                            }
                        }
                    }
                }
            }
            if ((props.name === 'routeForm' || props.name === 'goingBackForm') && (fieldName === 'sx:pbtCountryBusinessTrip' || fieldName === 'sx:pbtCity')) {
                if (updatetdValue[index].properties['sx:pbtCountryBusinessTrip'] && updatetdValue[index].properties['sx:pbtCity']) {
                    const travelForm = updatetdValue[index].properties['travelForm'];
                    if (travelForm?.length > 0) {
                        const placeInfo = props.settings.fields.find(f => f.name === 'placeInfo');
                        const selectedItem = placeInfo.fields.find(f => f.name === 'sx:pbtCountryBusinessTrip').values.data.find(item => item.value === updatetdValue[index].properties['sx:pbtCountryBusinessTrip']);
                        travelForm[0].properties['sx:tfPointArrival'] = `${updatetdValue[index].properties['sx:pbtCity']}, ${selectedItem?.label}`;
                        travelForm[0].properties['wasUpdated'] = true;
                    }
                }
            }
            if ((props.name === 'routeForm' || props.name === 'goingBackForm') && fieldName === 'travelForm' && index < updatetdValue.length - 1) {
                if (!oldValue || oldValue?.length <= 0 || oldValue[0].properties['sx:tfPointArrival'] !== updatetdValue[index].properties['travelForm'][0].properties['sx:tfPointArrival']) {
                    if (updatetdValue[index].properties['travelForm'][0].properties['sx:tfPointArrival']) {
                        const nextRoutForm = updatetdValue[index + 1];
                        if (nextRoutForm.properties['travelForm']?.length > 0) {
                            nextRoutForm.properties['travelForm'][0].properties['sx:tfPoint'] = updatetdValue[index].properties['travelForm'][0].properties['sx:tfPointArrival'];
                            nextRoutForm.properties['wasUpdated'] = true;
                            nextRoutForm.properties['travelForm'][0].properties['wasUpdated'] = true;
                        }
                    }
                }
            }
            // Виїзд наступного маршруту не може бути раніше за виїзд поточного, а зворотний
            // виїзд — раніше за виїзд туди, тому дати поза новими межами скидаються
            if (props.name === 'routeForm' && fieldName === 'travelForm') {
                const departureDate = getRouteDepartureDate(updatetdValue[index]);
                for (let i = index + 1; i < updatetdValue.length; i++) {
                    const nextTravelForm = updatetdValue[i].properties['travelForm'];
                    if (nextTravelForm?.length > 0 && isDateOutOfRange(nextTravelForm[0].properties['sx:tfDepartureDate'], departureDate, null)) {
                        nextTravelForm[0].properties['sx:tfDepartureDate'] = '';
                        nextTravelForm[0].properties['sx:tfArrivalDate'] = '';
                        nextTravelForm[0].properties['wasUpdated'] = true;
                        updatetdValue[i].properties['wasUpdated'] = true;
                    }
                }
                if (isDateOutOfRange(updatetdValue[index].properties['dateStartBack'], departureDate, null)) {
                    updatetdValue[index].properties['dateStartBack'] = '';
                }
            }
            updateValues(updatetdValue[index]);
            props.onChange({
                target: {
                    name: props.name,
                    value: updatetdValue
                }
            }, updatedFields);
        }
    }

    const onAddBtnClick = () => {
        if (props.onChange) {
            const newItem = {
                type: props.settings.formType,
                properties: {}
            };
            if (props.settings.formType === alfrescoTypes.businessTripPlace) {
                if (props.name === 'routeForm') {
                    newItem.properties['sx:btAccommodationRequired'] = true;
                }
                else if (props.name === 'goingBackForm') {
                    newItem.properties['sx:pbtReturnHome'] = true;
                }
                if (props.value?.length > 0) {
                    newItem.properties['sx:pbtDate'] = props.value[props.value.length - 1].properties['sx:pbtDateEnd'];
                }
            }
            if (props.settings.formType === alfrescoTypes.transitTripPlace) {
                newItem.properties['sx:btAccommodationRequired'] = true;
            }
            if (props.name === 'reportForm' || props.name === 'resultForm') {
                const newIndex = props.value?.length ?? 0;
                if (!expandedChildren.includes(newIndex)) {
                    setExpandedChildren(prevValue => [...prevValue, newIndex]);
                }
            }
            const subformFields = props.settings.fields.filter(f => f.type === formFieldTypes.subform);
            subformFields.forEach(field => {
                newItem.properties[field.name] = [{
                    type: field.formType,
                    properties: {}
                }];
                if (field.name === 'travelForm' && props.name === 'goingBackForm') {
                    newItem.properties[field.name][0].properties['sx:pbtReturnHome'] = true;
                }
                if (field.name === 'travelForm' && props.name === 'routeForm') {
                    if (props.value?.length > 0 && props.value[props.value.length - 1].properties['travelForm']?.length > 0) {
                        newItem.properties[field.name][0].properties['sx:tfPoint'] = props.value[props.value.length - 1].properties['travelForm'][0].properties['sx:tfPointArrival'] || '';
                    }
                }
            });


            let updatedValue = [];
            if (props.value) {
                updatedValue = structuredClone(props.value);
            }
            updatedValue.push(newItem);
            props.onChange({
                target: {
                    name: props.name,
                    value: updatedValue
                }
            });
        }
    }

    const onRemoveBtnClick = (index) => {
        if (props.onChange) {
            yesNoDialog(i18next.t('attentionTitle'), props.settings.removeMessageText ?? i18next.t('removeElementsMessage'), async () => {
                const updatedValue = structuredClone(props.value);
                updatedValue.splice(index, 1);
                props.onChange({
                    target: {
                        name: props.name,
                        value: updatedValue
                    }
                });
            });
        }
    }

    const isNeedUpdateCarInfo = (properties) => {
        if (props.formMode !== formModes.read) {
            if (props.settings.userNodeRef === userContext?.nodeRef || props.settings.userName === userContext?.login) {
                if (properties['sx:ciBrandCar'] && properties['sx:ciBrandCar'] !== userContext.userCarInfo?.brand) {
                    return true;
                }
                if (properties['sx:ciRegNumerInfo'] && properties['sx:ciRegNumerInfo'] !== userContext.userCarInfo?.number) {
                    return true;
                }
                if (properties['sx:ciReleaseYear'] && properties['sx:ciReleaseYear'].toString() !== userContext.userCarInfo?.releaseYear) {
                    return true;
                }
                if (properties['sx:ciEngineType'] && properties['sx:ciEngineType'] !== userContext.userCarInfo?.engineType) {
                    return true;
                }
                if (properties['sx:ciEngineDisplacement'] && properties['sx:ciEngineDisplacement'] !== userContext.userCarInfo?.engineVolume) {
                    return true;
                }
                if (properties['sx:ciCarColor'] && properties['sx:ciCarColor'] !== userContext.userCarInfo?.color) {
                    return true;
                }
                if (properties['sx:ciEngineType'] && properties['sx:ciEngineType'] === userContext.userCarInfo?.engineType) {
                    if (properties[`sx:ciFuelType_${userContext.userCarInfo.engineType}`] && properties[`sx:ciFuelType_${userContext.userCarInfo.engineType}`] !== userContext.userCarInfo.fuelType) {
                        return true;
                    }
                }
            }
        }
        return false;
    }

    const getVisibleFields = (fields) => (fields ?? []).filter(f => f.visible && isFieldVisibleOnStep(f, props.navigationIndex));

    const renderGroup = (group, props, value, index) => {
        return <div className={'form-section' + (group.showBorder ? ' show-border' : '') + (group.className ? ` ${group.className}` : '')}>
            {(group.label || group.description) && <div className='form-section-header'>
                {group.label && <div className='form-section-label'>{group.label}</div>}
                {group.description && <div className='form-section-description'>{group.description}</div>}
            </div>}
            <div className='form-section-content'>
                {getVisibleFields(group.fields).map(field => renderField(field, props, value, index))}
            </div>
        </div>
    }

    // formgroup is a card with the fields inside it, drawn without labels and separated by dividers
    const renderFormGroup = (group, props, value, index) => {
        return <div className={'form-group-card' + (group.className ? ` ${group.className}` : '')}>
            {getVisibleFields(group.fields).filter(f => !f.formModes || f.formModes.includes(props.formMode))
                .map(field => renderField(field, props, value, index, true))}
        </div>
    }

    const renderField = (field, props, value, index, insideFormGroup = false) => {
        if (field.type === formFieldTypes.group) {
            return renderGroup(field, props, value, index);
        }
        else if (field.type === formFieldTypes.formGroup) {
            return renderFormGroup(field, props, value, index);
        }
        else {
            // inside formgroup the label is shown as a placeholder of the field itself
            const settings = insideFormGroup ? { ...field, placeholder: field.placeholder ?? field.label } : field;
            const fieldValue = getFieldValue(field, value.properties);
            return <div key={field?.name} name={field.name} className={`form-group ${field.type}-field col-xs-12${field.className ? ` ${field.className}` : ''}`}>
                <InputContainer {...settings} hideLabel={insideFormGroup} check={props.submitted} required={(field.required && props.formMode !== formModes.read) || field.alwaysRequired} value={fieldValue}>
                    <FormField settings={settings} parentName={`${props.name}---${index}`} value={fieldValue} onChange={(e, updatedFields) => onChange(e, index, updatedFields)} formMode={props.formMode} submitted={props.submitted} navigationIndex={props.navigationIndex} />
                </InputContainer>
            </div>
        }
    }

    const renderItemHeader = (index) => {
        if (props.settings.itemLabel) {
            const canRemove = (props.settings.minCount >= 0 ? props.value.length > props.settings.minCount : props.value.length > 1) &&
                props.formMode !== formModes.read && !props.value[index].properties['sx:autofillObject'] && !props.settings.disabled;
            const removeClickHandle = (e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemoveBtnClick(index);
            }
            const newDesign = props.settings.newDesign;
            const hideCounter = props.settings.hideCounter;
            // Один транспорт на всі маршрути: власний вибір транспорту в маршруті прихований,
            // тому його вид показується припискою біля заголовка маршруту, як у дизайні
            const transportLabel = props.settings.hideTransportSelect
                ? findField(props.settings.fields, 'sx:pbtMethodTransportation')?.values?.data
                    ?.find(item => item.value === props.value[index].properties['sx:pbtMethodTransportation'])?.label
                : '';
            // На кроці проживання біля заголовка показується відрізок маршруту або транзитної
            // зупинки: «Звідки → Куди (початок – кінець)»
            let residenceRouteText = '';
            if (props.navigationIndex === 3) {
                if (props.name === 'routeForm') {
                    const travelForm = props.value[index].properties['travelForm']?.[0];
                    const { residenceMinDate, residenceMaxDate } = getRouteDateLimits(index, 'routeForm');
                    // по Україні пункти маршруту зберігаються як «Місто, Область» — в заголовку
                    // лишається тільки місто, а за кордоном рядок показується як є
                    const cityOf = (point) => props.settings.tripDestination === tripDestinations.ukraine ? stripRegion(point) : point;
                    residenceRouteText = getResidenceHeaderText(cityOf(travelForm?.properties['sx:tfPoint']), cityOf(travelForm?.properties['sx:tfPointArrival']), residenceMinDate, residenceMaxDate);
                }
                else if (props.name.endsWith('---transitForm')) {
                    // зупинка їде з попередньої зупинки, а перша — з пункту виїзду маршруту
                    const previousCity = index > 0 ? props.value[index - 1].properties['sx:pbtCity'] : props.settings.departurePoint;
                    const { residenceMinDate, residenceMaxDate } = getTransitDateLimits(index);
                    residenceRouteText = getResidenceHeaderText(previousCity, props.value[index].properties['sx:pbtCity'], residenceMinDate, residenceMaxDate);
                }
            }
            const headerContent = <div className={'item-header' + (newDesign ? ' new-design' : '')}>
                <div className='item-header-block'>
                    <div className='item-header-label-block'>
                        {!newDesign && !hideCounter && <div className='item-header-index'>{index + 1}</div>}
                        {props.settings.itemLabel && !props.settings.hideItemLabel && <div className='item-header-label'>
                            {!hideCounter ? `${props.settings.itemLabel} ${index + 1}` : props.settings.itemLabel}
                        </div>}
                        {transportLabel && <div className='item-header-transport'>{transportLabel}</div>}
                        {residenceRouteText && <div className='item-header-route'>{residenceRouteText}</div>}
                    </div>
                    {props.settings.showItemHeaderPreview &&
                        <div className='item-header-preview'>
                            {getSubformHeaderPreview(props.settings.fields, props.value[index].properties)}
                        </div>
                    }
                    {props.settings.showItemHeaderInfo && <div className='item-header-info'>
                        {getSubformHeaderInfo(props.settings.fields, props.value[index].properties)}
                    </div>}
                </div>
                {canRemove && (props.settings.removeBtnText
                    // не button, бо заголовок елемента сам може бути кнопкою акордеона
                    ? <div className='remove-text-btn' role='button' onClick={removeClickHandle}>{props.settings.removeBtnText}</div>
                    : <div className='remove-btn' onClick={removeClickHandle} />)}
            </div>;
            if (props.settings.collapsibleItems) {
                return <Accordion.Header>
                    {headerContent}
                </Accordion.Header>
            }
            else {
                return <div className='accordion-header'>
                    {headerContent}
                </div>
            }
        }
    }

    return <Accordion
        activeKey={expanded ? '0' : ''}
        onSelect={() => setExpanded(!expanded)}
        className={'subform-accordion' + (props.settings.collapsible ? '' : ' hide-collapse') + ((!props.settings.label && !props.settings.description) ? ' without-header' : '')}
    >
        <div className={'form-section' + (props.settings.showBorder ? ' show-border' : '')}>
            <Accordion.Item eventKey="0">
                <Accordion.Header>
                    {(props.settings.label || props.settings.description) && <div className='form-section-header'>
                        {props.settings.label && <div className='form-section-label'>{props.settings.label}</div>}
                        {props.settings.description && <div className='form-section-description'>{props.settings.description}</div>}
                        {props.settings.showHeaderPreview && props.value?.length > 0 &&
                            <div className='header-preview'>
                                {getSubformHeaderPreview(props.settings.fields, props.value[0].properties)}
                            </div>
                        }
                    </div>}
                </Accordion.Header>
                <Accordion.Body>
                    <div className='form-section-content'>
                        {props.value?.map((value, index) => {
                            if (props.settings.showOnlyFirstItem && index > 0) return null;
                            return <Accordion
                                activeKey={expandedChildren.includes(index) ? '0' : ''}
                                onSelect={() => {
                                    expandedChildren.includes(index) ?
                                        setExpandedChildren(expandedChildren.filter(item => item !== index)) :
                                        setExpandedChildren(prevValue => [...prevValue, index]);
                                }
                                }
                                className={'subform-accordion' + (props.settings.collapsibleItems ? '' : ' hide-collapse') +
                                    (!props.settings.itemLabel ? ' without-header' : '') +
                                    (props.settings.hideItemBorder ? ' without-border' : '')
                                }
                            >
                                <div className='subform' childIndex={index} >
                                    <Accordion.Item eventKey="0">
                                        {renderItemHeader(index)}
                                        <Accordion.Body>
                                            {getVisibleFields(getFields(index)).map(item => renderField(item, props, value, index))}
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </div>
                            </Accordion>
                        })}
                        {props.settings.multiple && !props.settings.disabled && props.formMode !== formModes.read &&
                            (!props.settings.maxCount || props.settings.maxCount === 0 || props.settings.maxCount > props.value?.length) &&
                            <div className={'add-btn-contaner' + (props.settings.addBtnHelpText ? " with-help-text" : "")}>
                                <Button variant="secondary add-btn" onClick={onAddBtnClick}>{props.settings.addBtnText}</Button>
                                {props.settings.addBtnHelpText && <OverlayTrigger
                                    placement="left"
                                    delay={{ show: 250, hide: 400 }}
                                    overlay={<Tooltip>
                                        {props.settings.addBtnHelpText}
                                    </Tooltip>}
                                >
                                    <div className='help-btn' />
                                </OverlayTrigger>}
                            </div>
                        }
                    </div>
                </Accordion.Body>
            </Accordion.Item>
        </div>
    </Accordion>
}
export default SubForm;